import { useState } from "react"

import { TextField } from "~/components/ui-components/v2"

/**
 * The System Pricing margin draft, for any proposal kind. Each kind supplies
 * its unit, bounds, decimal places and copy; the draft follows the saved
 * value until the seller edits it, and an edit that parses back to the saved
 * value is not a change.
 */
export type MarginDraftRules = {
  /** Most decimal places a draft may carry. */
  decimals: number
  /** Inclusive bounds; null leaves that side open. */
  min: number | null
  max: number | null
  messages: { invalid: string; tooLow: string; tooHigh: string }
  /** The saved value as the draft first shows it. */
  format: (value: number) => string
}

export type MarginDraftParse =
  | { value: null; error: string }
  | { value: number; error: null }

export const parseMarginDraft = (
  raw: string,
  rules: MarginDraftRules,
): MarginDraftParse => {
  const trimmed = raw.trim()
  const pattern = new RegExp(`^(?:0|[1-9]\\d*)(?:\\.\\d{1,${rules.decimals}})?$`)
  const value = Number(trimmed)
  if (trimmed === `` || !pattern.test(trimmed) || !Number.isFinite(value)) {
    return { value: null, error: rules.messages.invalid }
  }
  if (rules.min != null && value < rules.min) {
    return { value: null, error: rules.messages.tooLow }
  }
  if (rules.max != null && value > rules.max) {
    return { value: null, error: rules.messages.tooHigh }
  }
  return { value, error: null }
}

export type MarginDraft = {
  draft: string
  setDraft: (draft: string) => void
  /** The draft's value, or null while it is invalid. */
  parsed: number | null
  error: string | null
  /** An unsaved edit: a valid value other than the saved one, or any invalid text. */
  dirty: boolean
}

/**
 * Seeds the draft from `saved` and re-seeds it, during render, whenever
 * `seedKey` changes — a save or refresh that moved the server's value.
 */
export const useMarginDraft = ({
  saved,
  seedKey,
  rules,
}: {
  saved: number
  seedKey: string
  rules: MarginDraftRules
}): MarginDraft => {
  const [draft, setDraft] = useState(() => rules.format(saved))
  const [draftSeedKey, setDraftSeedKey] = useState(seedKey)
  if (draftSeedKey !== seedKey) {
    setDraftSeedKey(seedKey)
    setDraft(rules.format(saved))
  }
  const { value: parsed, error } = parseMarginDraft(draft, rules)
  // The untouched draft is clean even when it shows a saved value rounded to
  // the field's decimals (V1 may store a margin at full precision).
  const dirty =
    draft.trim() === rules.format(saved)
      ? false
      : parsed != null
        ? parsed !== saved
        : true
  return { draft, setDraft, parsed, error, dirty }
}

/** `$` is Battery's flat dollars; `$/W` is Solar's per-watt margin. */
export type MarginUnit = `$/W` | `$`

/** The Margin input in the totals card. */
export const MarginField = ({
  unit,
  draft,
  error,
  disabled,
  onChange,
}: {
  unit: MarginUnit
  draft: string
  error: string | null
  disabled: boolean
  onChange: (value: string) => void
}): JSX.Element => (
  <TextField
    label="Margin"
    labelSrOnly
    leadingAddon="$"
    trailingAddon={unit === `$/W` ? `/W` : undefined}
    inputMode="decimal"
    autoComplete="off"
    value={draft}
    error={error}
    disabled={disabled}
    onChange={(event) => {
      onChange(event.target.value)
    }}
    className={`ml-auto -mr-3 ${unit === `$/W` ? `w-40` : `w-36`} text-left [&_input]:text-right`}
  />
)
